import {
  setPlumaContextValue,
  setPlumaTheme,
  setPlumaIconManager,
  plumaContextValueStore as legacyPlumaContextValueStore,
  plumaThemeStore as legacyPlumaThemeStore,
  plumaIconManagerStore as legacyPlumaIconManagerStore,
} from '@customerio/hydra-pluma-state';
import { IconManager } from '@customerio/pluma-icons';
import { defaultTheme, newTheme, darkTheme } from '@customerio/pluma-components/css';
import type { PlumaTheme } from '@customerio/pluma-components/css';
import { PlumaProvider } from '@customerio/pluma-components/react';
import type { PlumaContextValue } from '@customerio/pluma-components/react';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { forwardRef, useLayoutEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import type { LinkComponent, PlumaProviderProps } from '@customerio/pluma-components/react';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { shouldProcessLinkClick } from '../../utils/navigation';
import { appendMasqParam } from '@/utils/masq';
import { isEmptyString } from '@/utils/string';
import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useThemeContext } from '@/context/theme';

const iconManager = new IconManager();
setPlumaIconManager(iconManager);

const UNSAFE_ICON_SIZE_OVERRIDE_LS_KEY = 'hydra:unsafe_icon_size_override';
let defaultIconSizeVersion: 'v1' | 'v2' = 'v2';
const iconSizeOverride = localStorage.getItem(UNSAFE_ICON_SIZE_OVERRIDE_LS_KEY);
if (iconSizeOverride != null && (iconSizeOverride === 'v1' || iconSizeOverride === 'v2')) {
  defaultIconSizeVersion = iconSizeOverride;
}

export const plumaContextValueStore = atom<Partial<PlumaContextValue> | null>({
  componentFlags: {
    unsafe_useLegacyButton: true,
    unsafe_useLegacyTextField: true,
    unsafe_useLegacyTextArea: true,
    unsafe_useLegacyLink: true,
  },
  componentConfig: {
    PlumaIcon: {
      defaultSizeVersion: defaultIconSizeVersion,
    },
  },
  iconManager,
});

// TODO: Remove hydra-pluma-state once child apps are updated to use the new stores
setPlumaContextValue(plumaContextValueStore.get() ?? {});
legacyPlumaIconManagerStore.subscribe((value) => {
  plumaContextValueStore.set({ ...plumaContextValueStore.get(), iconManager: value as unknown as IconManager });
});
legacyPlumaContextValueStore.subscribe((value) => {
  plumaContextValueStore.set({
    ...plumaContextValueStore.get(),
    ...value,
  });
});

export const plumaThemeStore = atom<PlumaTheme | null>(defaultTheme);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
setPlumaTheme(plumaThemeStore.get() as any);
legacyPlumaThemeStore.subscribe((value) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plumaThemeStore.set(value as any);
});

/**
 * PlumaLinkDefinition connects the Pluma design system to the
 * react-router-dom Link component. It allows for the use of
 * react-router-dom's navigation features while still using
 * Pluma's styling and theming.
 */
const PlumaLinkDefinition = forwardRef(function (props, ref) {
  const { href, replace, onClick, ...restProps } = props as any;

  const safeNavigate = useSafeNavigate();

  // Automatically append masq param if masquerading
  const finalHref = !isEmptyString(href) ? appendMasqParam(href) : '';

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => {
    onClick?.(event);

    // Check if the consumer's onClick handler prevented the default behavior
    if (event.defaultPrevented) {
      return;
    }

    if (isEmptyString(finalHref)) {
      event.preventDefault();
      return;
    }

    if (!shouldProcessLinkClick(event)) {
      return;
    }

    // Let browser handle external links (mailto:, tel:, http://, https://)
    const targetHref = event.currentTarget.getAttribute('href') ?? '';
    if (/^(mailto:|tel:|https?:\/\/)/.test(targetHref)) {
      return; // Let browser handle it naturally
    }

    event.preventDefault();
    event.stopPropagation();

    safeNavigate(targetHref, opts);
  };

  return <ReactRouterLink to={finalHref} replace={replace} {...restProps} onClick={onLinkClick} ref={ref} />;
}) as LinkComponent;

/**
 * Configures Pluma for Root and EmbedRoot components,
 * set globally so that other apps can consume our configuration.
 */
export function ConnectedPlumaProvider(props: PlumaProviderProps) {
  const plumaContextValue = useStore(plumaContextValueStore);
  const plumaTheme = useStore(plumaThemeStore);
  const themeContext = useThemeContext();

  const withNewTokens = useFeatureFlag(FeatureFlag.ContentTokens2026);

  useLayoutEffect(() => {
    if (themeContext.isDarkMode) {
      plumaThemeStore.set(darkTheme);
      setPlumaTheme(darkTheme as any);
    } else {
      if (withNewTokens) {
        plumaThemeStore.set(newTheme);
        setPlumaTheme(newTheme as any);
      } else {
        plumaThemeStore.set(defaultTheme);
        setPlumaTheme(defaultTheme as any);
      }
    }
  }, [withNewTokens, themeContext.isDarkMode]);

  const resolvedTheme =
    plumaTheme ?? (themeContext.isDarkMode ? darkTheme : undefined) ?? (withNewTokens ? newTheme : defaultTheme);

  return (
    <PlumaProvider
      theme={resolvedTheme}
      iconManager={plumaContextValue?.iconManager ?? iconManager}
      componentFlags={plumaContextValue?.componentFlags}
      componentConfig={plumaContextValue?.componentConfig}
      shouldApplyCssIsolation={true}
      linkComponent={PlumaLinkDefinition}
      {...props}
    />
  );
}
